<template>
  <a-modal
    title="验证码"
    :closable="true"
    v-model="visible"
    @cancel="handleCancel"
    :maskClosable="true"
    :width="400"
    :footer="null"
    :zIndex="300000000"
  >
    <div class="captcha-container">
      <!-- 验证码图片显示区域 -->
      <div class="captcha-image-wrapper">
        <img
          v-if="captchaImageUrl"
          :src="captchaImageUrl"
          alt="验证码图片"
          class="captcha-image"
          @click="refreshCaptcha"
        />
        <div v-else class="captcha-loading">
          <a-spin size="large" />
          <p>加载验证码中...</p>
        </div>
        <div class="refresh-tip">点击图片刷新验证码</div>
      </div>

      <!-- 验证码输入框 -->
      <div class="captcha-input-wrapper">
        <a-form layout="vertical">
          <a-form-item label="请输入验证码">
            <a-input
              v-model="captchaCode"
              placeholder="请输入图片中的验证码"
              size="large"
              maxlength="6"
              :disabled="loading"
              @keyup.enter="handleConfirm"
            />
          </a-form-item>
        </a-form>
      </div>

      <!-- 操作按钮 -->
      <div class="captcha-actions">
        <a-button @click="handleCancel" :disabled="loading">取消</a-button>
        <a-button type="primary" @click="handleConfirm" :loading="loading" style="margin-left: 12px">确认</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { getCaptchaCode } from '@/api/login'

export default {
  name: 'CaptchaModal',
  data() {
    return {
      visible: false,
      loading: false,
      captchaImageUrl: '',
      captchaId: '',
      captchaCode: '',
      captchaType: '',
    }
  },
  methods: {
    /**
     * 获取验证码图片
     * @description 调用后端接口获取验证码图片数据
     */
    async getCaptchaImage() {
      try {
        this.loading = true
        /** @request
         * POST /login/captchaImge
         * 获取验证码图片接口
         * response: {
         *   state: number // 状态码，200为成功
         *   data: {
         *     image: string // base64编码的图片数据
         *     key: string   // 验证码标识key，用于后续验证
         *   }
         *   message: string // 响应消息
         * }
         */
        const res = await getCaptchaCode()

        if (res.code === 1 && res.data) {
          if (res.data.base64) {
            // 如果返回的图片已包含data:image前缀，直接使用
            this.captchaImageUrl = res.data.base64
            // 保存验证码key用于后续验证
            this.captchaId = res.data.captchaId
          } else {
            this.$message.error('验证码图片获取失败')
          }
        } else {
          this.$message.error((res && res.message) || '获取验证码失败')
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('获取验证码图片失败:', error)
        this.$message.error('网络错误，请重试')
      } finally {
        this.loading = false
      }
    },

    /**
     * 刷新验证码
     * @description 重新获取验证码图片并清空输入框
     */
    async refreshCaptcha() {
      this.captchaCode = ''
      await this.getCaptchaImage()
    },

    /**
     * 显示验证码弹窗
     * @description 对外暴露的方法，用于显示弹窗并获取验证码
     * @param {string} type - 验证码类型，透传给使用方（可为空）
     */
    async showModal(type = '') {
      // 重置表单数据
      this.captchaType = type || ''
      this.captchaCode = ''
      this.captchaImageUrl = ''
      this.captchaId = ''

      // 显示弹窗
      this.visible = true

      // 获取验证码图片
      await this.getCaptchaImage()
    },

    /**
     * 确认按钮处理
     * @description 验证输入并触发确认事件
     */
    async handleConfirm() {
      if (!this.captchaCode || !this.captchaCode.trim()) {
        this.$message.error('请输入验证码')
        return
      }
      try {
        this.loading = true
        // 触发确认事件，传递验证码和key
        this.$emit('confirm', this.captchaCode.trim(), this.captchaId, this.captchaType)
        // 关闭弹窗
        this.visible = false
      } catch (error) {
        // 保留 loading 处理的一致性
        // eslint-disable-next-line no-console
        console.log('确认处理失败:', error)
      } finally {
        this.loading = false
      }
    },

    /**
     * 取消按钮处理
     * @description 关闭弹窗并触发取消事件
     */
    handleCancel() {
      this.$emit('cancel')
      this.visible = false
    },
  },
}
</script>

<style scoped lang="less">
.captcha-container {
  display: flex;
  flex-direction: column;
  gap: 20px;

  .captcha-image-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    .captcha-image {
      width: 150px;
      height: 50px;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        border-color: #40a9ff;
        box-shadow: 0 0 4px rgba(64, 169, 255, 0.3);
      }
    }

    .captcha-loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 150px;
      height: 50px;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      background: #fafafa;

      p {
        margin: 8px 0 0 0;
        font-size: 12px;
        color: #666;
      }
    }

    .refresh-tip {
      font-size: 12px;
      color: #999;
      text-align: center;
    }
  }

  .captcha-input-wrapper {
    .ant-form-item {
      margin-bottom: 0;
    }
  }

  .captcha-actions {
    display: flex;
    justify-content: center;
    padding-top: 10px;
  }
}
</style>
